<script setup lang="ts">
import { toRef, computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import type { LocaleCode } from '../../../utils/i18n'
import MultiSelect from '@vueform/multiselect'
import _ from 'lodash'
import { get, put } from '@rails/request.js'

type Series = {
  id: number | null,
  name: string
}

type Option = {
  value: Series,
  label: string,
}
const props = defineProps<{
  locale: LocaleCode,
  data: {
    series: Series,
    paths: {
      update_game_series_path: string,
      game_series_path: string,
      series_autocomplete_path: string
    }
  }
}>()

const { t } = useI18n({ locale: props.locale })
const gameSeries = toRef(props.data, 'series')

const initialValue = computed(() => {
  return { value: gameSeries.value, label: gameSeries.value.name }
})

const selectedSeries = ref<Option | null>(initialValue.value)

const searchOptions = async(url: string, query: string): Promise<Option[] | null> => {
  const response = await get(url, {
    responseKind: "json",
    query: { name: query }
  })
  if(response.ok) {
    const json = await response.json
    return json.serieses.map((sg: Series) => {
      return { value: sg, label: sg.name }
    })
  }
  return null;
}

const onSubmit = async() => {
  const response = await put(props.data.paths.update_game_series_path, {
    responseKind: "json",
    body: {
      game_series: selectedSeries.value?.value ||
        { series_id: null, name: null, locale: null }
    }
  })
  if(response.ok) {
    const response = await get(props.data.paths.update_game_series_path, {
      responseKind: "turbo-stream"
    })
  } else {
    alert(t('error_occured'))
  }
  return null;
}

const autocomplete = async (query: string) => {
  return await searchOptions(props.data.paths.series_autocomplete_path, query);
}

const selectSeries = (e: { originalEvent: Event, value: Series } | undefined) => {
  if(e) {
    selectedSeries.value =
      {
        label: e.value.name,
        value: (typeof e.value == 'string') ? {
          id: null,
          name: e.value
        } : e.value
      }
  } else {
    selectedSeries.value = null;
  }
}

</script>

<template lang="pug">
form
  div.modal__body(class="uk-modal-body" uk-overflow-auto)
    div.games__modal-series
      div.games__modal-series-description
        p.games__modal-series-text(v-html="t('description_1')")
        div.games__modal-series-value
          MultiSelect(
            :value="initialValue"
            @change="selectSeries"
            :options="autocomplete"
            :placeholder="t('placeholder')"
            :searchable="true"
            :createOption="true"
            :filter-results="false"
            :resolve-on-load="false"
            :object="true"
            track-by="label"
            :delay="300"
            :noResultsText="t('no_results')"
            :noOptionsText="t('no_options')"
            id="game_series_series_id"
          )
        p.games__modal-series-caption {{ t('description_2') }}
        p.games__modal-series-text(v-html="t('description_3')")
      div.games__modal-series-expander.closed(id="series_guidance")
        div.games__modal-series-guidance
          p.games__modal-series-text(v-html="t('guidance_1')")
          p.games__modal-series-text(v-html="t('guidance_2')") 
          p.games__modal-series-text(v-html="t('guidance_3')") 
          p.games__modal-series-text(v-html="t('guidance_4')")
        div.games__modal-series-button(href="#series_guidance" uk-scroll uk-toggle="target: #series_guidance; cls: closed")
          div.games__modal-series-button-open {{ t('guidance_button.open') }}
          div.games__modal-series-button-close {{ t('guidance_button.close') }}

  div.modal__footer(class="uk-modal-footer uk-text-center")
    button(class="uk-button uk-button-default uk-modal-close" type="button")
      | {{ t('close') }}
    button(
      class="uk-button uk-button-primary uk-modal-submit"
      type="button"
      @click="onSubmit"
    )
      | {{ t('save') }}
</template>

<i18n lang="json">{
  "ja": {
    "description_1": "このゲームを、指定のシリーズに加えることができます。<br>前作や次作があるにも関わらず何も登録されていない場合は、お手数ですが登録にご協力いただけると幸いです。",
    "description_2": "上記にシリーズを入力し、リストが出てきた場合は該当するものを選択してください。",
    "description_3": "リストが表示されない・該当するものがない場合は、入力後にエンターを押してください。新しい「シリーズ」タグが生成されます。<br>登録のガイドラインおよび登録例については以下の説明をお読みください。",
    "guidance_button": {
      "open": "シリーズ登録について",
      "close": "ガイドラインを閉じる"
    },
    "placeholder": "1つだけ登録できます",
    "guidance_1": "シリーズとは、一般的にデベロッパー側が後継作と公称する一連の作品を、1つにまとめたものです。<br>基本的に、ゲーム性やプレイ内容は前作から継承されていますが、大幅に変わっている場合でもタイトルの末尾でナンバリング化しているケース（2やIIIなど）が非常に多いです。",
    "guidance_2": "<span class='tag'>スーパーマリオ</span><br>これは、スーパーマリオブラザーズ2、スーパーマリオワールド、スーパーマリオブラザーズ ワンダー等が登録するシリーズ名です。",
    "guidance_3": "<span class='tag'>マリオカート</span><br>これは、スーパーマリオカート、マリオカート64、マリオカート8等が登録するシリーズ名です。",
    "guidance_4": "スーパーマリオカートは「マリオカート」シリーズではありますが、「スーパマリオ」シリーズではないことに注意してください。（「スーパーマリオ」シリーズは、サイドスクロールのプラットフォームアクションゲームです。）<br>「スーパーマリオ」シリーズの各ゲームと、「マリオカート」シリーズの各ゲームは、それぞれ「マリオ」フランチャイズで繋がりを持っています。",
    "save": "保存",
    "close": "閉じる",
    "no_results": "シリーズが見つかりませんでした",
    "no_options": "シリーズ名を入力してください",
    "error_occured": "エラーが発生しました"
  }
}</i18n>

<style src="@vueform/multiselect/themes/default.css"></style>
